import React, { useContext, useEffect, useRef, useState } from 'react';
import { FaPaperPlane } from "react-icons/fa";
import { FaCheckDouble } from "react-icons/fa6";
import { RxCrossCircled } from "react-icons/rx";
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { UserRequestTable } from '../../_core/_model.ts';
import { AuthContext } from '../../context/AuthContext.js';
import { defaultTheme } from '../../styles/themes/defaultTheme.js';
import ApprovalTable from '../Tables/ApprovalTable.tsx';
import DashChart from './DashChart.js';
import { AnimatePresence, motion } from 'framer-motion';

const HomeContainer = styled.div`
  padding: 0;
  margin: 0 auto;
  height: 100%;
  width: 100%;
`;

const WelcomeSection = styled.section`
  text-align: center;
  padding: 0.5rem 0;
  margin-bottom: 1rem;
`;

const WelcomeTitle = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => defaultTheme.colors.primary};
  font-family: ${({ theme }) => defaultTheme.fonts.secondary};
`;

const WelcomeText = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => defaultTheme.colors.textSecondary};
  max-width: 700px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 0px 21px;
`;

const FeatureCard = styled.div<{ selected: boolean }>`
  background-color: ${({ theme }) => defaultTheme.colors.card};
  border-radius: ${({ theme }) => defaultTheme.borderRadius.large};
  padding: 1rem;
  box-shadow: ${({ theme }) => defaultTheme.shadows.small};
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;
  border: ${({ selected, theme }) =>
    selected ? `2px solid ${defaultTheme.colors.primary}` : '2px solid transparent'};
  transform: ${({ selected }) => (selected ? 'scale(1.03)' : 'scale(1)')};

  &:hover {
    transform: scale(1.03);
    box-shadow: ${({ theme }) => defaultTheme.shadows.medium};
  }

  &::after {
    content: ${({ selected }) => (selected ? `''` : 'none')};
    position: absolute;
    top: 6px;
    right: 6px;
    width: 10px;
    height: 10px;
    background-color: ${({ theme }) => defaultTheme.colors.primary};
    border-radius: 50%;
    box-shadow: 0 0 10px ${({ theme }) => defaultTheme.colors.primary};
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => defaultTheme.colors.primary};
`;

const FeatureDescription = styled.div`
  color: ${({ theme }) => defaultTheme.colors.textSecondary};
`;

const Home = () => {
  const { user } = useContext(AuthContext);
  const headerRef = useRef<HTMLDivElement>(null);
  const welcomeNode = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<UserRequestTable[] | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [dashboardHeight, setDashboardHeight] = useState(0);

  useEffect(() => {
    const node = headerRef.current;
    if (!node)
      return;

    const updateHeight = () => {
      requestAnimationFrame(() => {
        let h = node.getBoundingClientRect().height + 35;
        if (showWelcome && welcomeNode) {
          let tHeight = welcomeNode.current?.getBoundingClientRect().height;
          if (typeof tHeight != 'undefined')
            h += tHeight;
        }
        setDashboardHeight(h);
      })
    }
    const obs = new ResizeObserver(updateHeight);
    obs.observe(node);

    updateHeight();

    return () => obs.disconnect();
  }, [headerRef, showWelcome])

  const updateSelected = (item: string) => {
    if (item == selectedStatus)
      setSelectedStatus(null)
    else
      setSelectedStatus(item);
  }

  useEffect(() => {
    setTimeout(() => {
      if (sessionStorage.getItem("welcome") == null) {
        sessionStorage.setItem("welcome", "true");
        setShowWelcome(false);
      }
      else
        setShowWelcome(false)
    }, 10000);
  }, [])

  return (
    <HomeContainer>
      {
        showWelcome &&
        <AnimatePresence>
          <motion.div
            ref={welcomeNode}
            initial={{ opacity: 0, y: -36, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -36, scale: 0.95 }}
            transition={{ duration: 0.3 }}>
            <WelcomeSection>
              <WelcomeTitle>Welcome back, {user?.userFirstName || 'User'}!</WelcomeTitle>
              {/* <WelcomeText>
          This morning there are as many 20 users logged in and waiting for you to approve few of the items.
        </WelcomeText> */}
            </WelcomeSection>
          </motion.div>
        </AnimatePresence>
      }


      <FeaturesGrid ref={headerRef}>
        <FeatureCard className="liquid-effect" onClick={() => { updateSelected("requested") }} selected={selectedStatus === "requested"}>
          <FeatureTitle>
            <div className='d-flex flex-row align-items-center gap-2 text-warning'>
              <FaPaperPlane />
              Requested
            </div>
          </FeatureTitle>

          <FeatureDescription>
            <div className='d-flex flex-row align-items-center justify-content-between'>
              {/* <span style={{ color: defaultTheme.colors.warning }} className='fw-bold fs-2'>{rows != null ? rows?.filter((x) => x.status == "" || x.status.toLowerCase() == "requested").length : "0"}</span> */}
              <DashChart color={defaultTheme.colors.warning}
                rows={rows != null ? rows.filter((x) => x.status == "" || x.status.toLowerCase() == "requested") : null} />
            </div>
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard className="liquid-effect" onClick={() => { updateSelected("approved") }} selected={selectedStatus === "approved"}>
          <FeatureTitle>
            <div className='d-flex flex-row align-items-center gap-2 text-success'>
              <FaCheckDouble />
              Approved/Partially Approved
            </div>
          </FeatureTitle>

          <FeatureDescription>
            <div className='d-flex flex-row align-items-center justify-content-between'>
              {/* <span style={{ color: defaultTheme.colors.success }} className='fw-bold fs-2'>{rows != null ? rows.filter((x) => x.status.toLowerCase() == "approved" || x.status.toLowerCase() == "partially approved").length : "0"}</span> */}
              <DashChart color={defaultTheme.colors.success}
                rows={rows != null ? rows.filter((x) => x.status.toLowerCase() == "approved" || x.status.toLowerCase() == "partially approved") : null} />
            </div>
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard className="liquid-effect" onClick={() => { updateSelected("rejected") }} selected={selectedStatus === "rejected"}>
          <FeatureTitle>
            <div className='d-flex flex-row align-items-center gap-2 text-danger'>
              <RxCrossCircled />
              Rejected
            </div>
          </FeatureTitle>

          <FeatureDescription>
            <div className='d-flex flex-row align-items-center justify-content-between'>
              {/* <span style={{ color: defaultTheme.colors.danger }} className='fw-bold fs-2'>{rows != null ? rows.filter((x) => x.status.toLowerCase() == "rejected").length : "0"}</span> */}
              <DashChart color={defaultTheme.colors.danger}
                rows={rows != null ? rows.filter((x) => x.status.toLowerCase() == "rejected") : null} />
            </div>
          </FeatureDescription>
        </FeatureCard>
      </FeaturesGrid>
      {/* <div className='mt-3'>
        <DashboardOverview rows={rows} />
      </div> */}
      <div className='mt-3' style={{ height: `calc(100% - ${dashboardHeight}px`, width: "100%" }}>
        <ApprovalTable status={selectedStatus} needButton={true} pushData={setRows} />
      </div>
    </HomeContainer>
  );
};

export default Home;